import type {
    SelectMenuGroup,
    SelectMenuItem,
} from '@local/web-design-system/components/SelectMenu';
import { SelectMenu } from '@local/web-design-system/components/SelectMenu';
import Grid from '@mui/material/GridLegacy';
import type { ComponentType, ElementType } from 'react';
import { createElement, useEffect, useState } from 'react';

import { useStyles } from './OrgHubSelectMenu.styles';

export interface OrgHubSelectMenuProps {
    options: SelectMenuItem[];
    optionGroups?: SelectMenuGroup[];
    onSelected: (key: string) => void;
    defaultValue?: string;
    value?: string;
    variant?: 'light' | 'dark';
    width?: number;
    topSpacing?: number;
}

/**
 * A select dropdown for selecting options, works during the login sequence
 * or the content area (top nav bar) within the app
 *
 * @param options - key value a pair of options (make sure the key is unique)
 * @param optionGroups - org list with their related ids
 * @param onSelected - provides the selected element key to consuming component
 * @param defaultValue - selects a default organisation.
 *   Makes component uncontrolled where it manages its own state.
 * @param value - organisation gets set by the parent component.
 *   Makes component controlled where the state is managed from the outside.
 * @param variant - selects a white or black color scheme
 * @param width - controls the width of the dropdown
 * @param topSpacing - controls spacing between the dropdown and the popover
 */
export const OrgHubSelectMenu = ({
    options = [],
    optionGroups,
    onSelected,
    defaultValue,
    value,
    variant = 'dark',
    width,
    topSpacing,
}: OrgHubSelectMenuProps) => {
    const { classes } = useStyles({ variant, width, topSpacing });
    const [selectedOption, setSelectedOption] = useState<string | undefined>(defaultValue);
    useEffect(() => {
        if (defaultValue) {
            onSelected(defaultValue);
            setSelectedOption(defaultValue);
        }
    }, [defaultValue]);

    function triggerSelected(selectedValue: string) {
        // Update internal state only in uncontrolled mode
        if (defaultValue !== undefined) {
            setSelectedOption(selectedValue);
        }

        onSelected(selectedValue);
    }

    // This is to display the option group when the select menu is not expanded
    function prefixOptionGroupLabel(option: string) {
        if (!optionGroups) {
            return undefined;
        }
        const filteredOptions = optionGroups.find((optionGroup) =>
            optionGroup.optionIds.some((optionId) => optionId === option),
        );
        if (!filteredOptions) {
            return undefined;
        }
        const { label } = filteredOptions;
        return `${label}`;
    }

    function filterDisplayNames(item: string) {
        return item.replace(/\b(Central Server - )\b/g, '');
    }

    function selectMenuItems() {
        const optionList = [] as any;
        options.forEach((item) => {
            const OptionGroup = prefixOptionGroupLabel(item.key as string);
            optionList.push({
                key: item.key,
                label: () =>
                    selectMenuComponentItem(
                        OptionGroup as string,
                        typeof item.label === 'string'
                            ? filterDisplayNames(item.label)
                            : item.label,
                    ),
            });
        });
        return optionList;
    }

    function selectMenuComponentItem(OptionGroup: string, option: string | ComponentType<any>) {
        return (
            <Grid container direction="column" alignItems="flex-start">
                {OptionGroup && (
                    <Grid
                        className={classes.textAlign}
                        onMouseOver={applyTitleIfEllipsisItems}
                        onFocus={applyTitleIfEllipsisItems}
                        item
                    >
                        <span
                            className="optionGroup" // used to display: 'none' when the options are expanded
                        >
                            {OptionGroup}
                        </span>
                    </Grid>
                )}
                <Grid
                    className={classes.textAlign}
                    onMouseOver={applyTitleIfEllipsisItems}
                    onFocus={applyTitleIfEllipsisItems}
                    item
                >
                    {typeof option === 'string' ? (
                        <span className="option">{option}</span>
                    ) : (
                        createElement(option as ElementType)
                    )}
                </Grid>
            </Grid>
        );
    }

    function applyTitleIfEllipsisItems(e: any) {
        if (e.target.scrollWidth > e.target.clientWidth) {
            e.target.parentElement.parentElement.parentElement.setAttribute(
                'title',
                e.target.innerText,
            );
        }
    }

    function applyTitleIfEllipsisOptionGroups(e: any) {
        if (e.target instanceof HTMLLIElement && e.target.role == null) {
            if (e.target.scrollWidth > e.target.clientWidth) {
                e.target.setAttribute('title', e.target.innerText);
            }
        }
    }

    return (
        <div
            onMouseOver={applyTitleIfEllipsisOptionGroups}
            onFocus={applyTitleIfEllipsisOptionGroups}
        >
            <SelectMenu
                options={selectMenuItems()}
                optionGroups={optionGroups}
                onSelect={(key) => triggerSelected(key.toString())}
                defaultValue={selectedOption}
                value={value}
                classes={{
                    button: classes.selectMenuButton,
                    paper: classes.selectPaper,
                    menuItem: classes.selectMenuItem,
                    menuLabel: classes.selectMenuLabel,
                    labelContainer: classes.labelContainer,
                }}
                applyTraceMessage="option"
            />
        </div>
    );
};
